<template>
  <div id="UNBARRING" class="container-fixed offset-bottom-6">
    <VProgress v-if="isProgressVisible" class="offset-top-2 offset-bottom-2" :steps="steps" active="unbarring.access" />
    <div v-else class="offset-top-2" />
    <div class="align">
      <img v-if="partnerLogo" :src="`delegates/barring/content/businesspartner/${partnerLogo}`" alt="" class="offset-right-1" style="max-height: 200px" />
      <h3 v-if="partnerName" class="h1" v-html="partnerName"></h3>
    </div>

    <div v-if="isItsEnabled && !isAuthorized" class="notification notification-warning">
      <div class="notification-content" v-html="$tc('unbarring.manualAuthHintText', 'unbarring/its/retry?sessionId=' + sessionId)"> </div>
    </div>

    <p class="offset-bottom-2 offset-top-2" v-html="$to('unbarring.accessDescription')"></p>

    <VForm :form="form" @submit="onNext">
      <template v-if="captcha">
        <div class="form-input-set">
          <label for="image_captcha" v-html="$t('unbarring.captchaImageLabel')"></label>
          <div class="align">
            <img id="image_captcha" alt="captcha" :src="'data:image/gif;base64,' + captcha" class="offset-right-1" />
            <VButton
              id="captcha_reload"
              type="button"
              label="unbarring.captchaReloadLabel"
              class="btn-default"
              :disabled="!isCaptchaReloadAllowed"
              @click="$emit('captchaReload')"
            />
          </div>
        </div>

        <VInput name="captcha" :label="$t('unbarring.captchaLabel')" :hint="$t('errorcodes.CAPTCHA_INVALID')" required />
      </template>

      <div class="offset-top-2 text-right">
        <VButton type="button" class="offset-left-1 offset-bottom-1 btn-clean" label="unbarring.abort" @click="redirect" />
        <VButton type="submit" class="offset-left-1 offset-bottom-1 btn-default btn-brand" label="unbarring.submit" :disabled="isBusy" />
      </div>
    </VForm>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import TitleMixin from '@/mixins/title';
import UnbarringSteps from '@/mixins/unbarringSteps';
import Proxy from '@/proxies/UnbarringProxy';
import VForm from '@/components/Form/Form';
import VInput from '@/components/Form/Input';
import { initFields, focusFirstError } from '@/utils/form';
import { UNBARRING_OTP, UNBARRING_PIN } from '@route-types';

export default {
  name: 'UnbarringAccess',

  components: {
    VForm,
    VInput,
    VProgress: () => import('@/components/Progress'),
    VButton: () => import('@/components/Button'),
  },

  mixins: [TitleMixin, UnbarringSteps],

  props: {
    partnerLogo: {
      type: String,
      required: false,
      default: null,
    },

    partnerName: {
      type: String,
      required: false,
      default: null,
    },

    captcha: {
      type: String,
      required: false,
      default: null,
    },

    isCaptchaReloadAllowed: {
      type: Boolean,
      required: false,
    },

    isAuthorized: {
      type: Boolean,
      required: false,
    },

    hasPin: {
      type: Boolean,
      required: false,
    },

    isItsEnabled: {
      type: Boolean,
      required: false,
    },
  },

  data() {
    return {
      isBusy: false,
      form: initFields({ name: 'captcha' }),
    };
  },

  computed: {
    ...mapState('system', ['sessionId']),

    isProgressVisible() {
      return this.hasPin || !this.isAuthorized;
    },
  },

  watch: {
    captcha: {
      handler() {
        this.form.captcha.isHidden = !this.captcha;
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions('unbarring', ['redirect']),

    async onNext({ values }) {
      this.isBusy = true;
      try {
        await new Proxy().confirm(values);
        if (this.isAuthorized) {
          if (!this.hasPin) {
            this.redirect();
          } else {
            this.$router.push({ name: UNBARRING_PIN });
          }
        } else {
          this.$router.push({ name: UNBARRING_OTP });
        }
      } catch (error) {
        const code = error?.data?.code;
        if (code === 'INVALID_CAPTCHA') {
          this.form.captcha.setInvalid();
        } else {
          throw error;
        }
        focusFirstError(this.form);
        this.isBusy = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

.notification {
  padding-bottom: 16px;
  margin-top: 12px;
}
.h1 {
  display: inline-block;
  margin: 0;
}

#image_captcha {
  border: 1px solid #b2b2b2;
  border-radius: 4px;
}

@media screen and (max-width: #{$grid-small-max}px) {
  #captcha_reload {
    width: 100%;
  }
}

@media screen and (min-width: #{$grid-medium-min}px) {
  .align {
    display: flex;
    align-items: center;
  }
}
</style>
